import { z } from "zod";
//#region src/branding.schemas.ts
/**
* Shared per-tenant branding contract (CP source of truth → DP replica).
*
* `OrgBrandingSchema` is the single shape for: the CP store
* (`platform.organizations.branding`), the CP bot write API, the CP→DP sync
* payload, the DP store (`auth.organizations.branding`), the DP read
* projections (`/v1/auth/providers`, `/v1/auth/me`), the shell, and email.
* No package redefines branding fields — every consumer imports `z.infer<>`
* from here (DECISIONS D2). Types are co-located with the schema so the wire
* format and the TypeScript surface cannot drift.
*/
const HEX = /^#[0-9a-fA-F]{6}$/;
/** A logo/favicon reference: a first-party `/brand-assets/...` path or an external https URL.
*  Rejects any `..` path segment (defense-in-depth; ruling X-1). */
const assetRef = z.string().max(2048).refine((s) => (s.startsWith("/brand-assets/") || s.startsWith("https://")) && !s.split("/").includes(".."), "must be a /brand-assets/ path or https URL with no \"..\" segments");
const brandingIdentityFields = {
	productName: z.string().min(1).max(60).optional(),
	platformLabel: z.string().min(1).max(60).optional(),
	logo: z.object({
		lightUrl: assetRef.optional(),
		darkUrl: assetRef.optional()
	}).strict().optional(),
	faviconUrl: assetRef.optional()
};
const brandingColorFields = {
	primaryColor: z.string().regex(HEX).optional(),
	primaryColorDark: z.string().regex(HEX).optional(),
	accentSecondary: z.string().regex(HEX).optional(),
	accentSecondaryDark: z.string().regex(HEX).optional()
};
const brandingCopyFields = {
	loginTagline: z.string().max(200).optional(),
	loginSubtitle: z.string().max(300).optional(),
	loginHighlights: z.array(z.object({
		title: z.string().max(80),
		body: z.string().max(240)
	}).strict()).max(4).optional(),
	emailFromName: z.string().max(120).optional()
};
/** Dark-accent pairing rule (D6: no auto-derive). Shared by both tiers. */
const darkAccentRefine = (b) => !b.primaryColor || !!b.primaryColorDark;
const darkAccentMsg = {
	message: "primaryColorDark is required when primaryColor is set",
	path: ["primaryColorDark"]
};
/** The STORED per-org brand record. Every field optional → a fully-unset org renders neutral. */
const OrgBrandingSchema = z.object({
	...brandingIdentityFields,
	...brandingColorFields,
	copy: z.object(brandingCopyFields).strict().optional()
}).strict().refine(darkAccentRefine, darkAccentMsg);
/** The DEPLOYMENT brand tier (WS3). Same vocabulary as OrgBranding + a CP-console wordmark
*  (`consoleName`, defaults to productName) and DP hero taglines (`heroTaglines`). Precedence:
*  per-org (OrgBranding) → deployment (this) → hardwired neutral. */
const DeploymentBrandingSchema = z.object({
	...brandingIdentityFields,
	...brandingColorFields,
	consoleName: z.string().min(1).max(80).optional(),
	copy: z.object({
		...brandingCopyFields,
		heroTaglines: z.array(z.string().min(1).max(120)).max(6).optional()
	}).strict().optional()
}).strict().refine(darkAccentRefine, darkAccentMsg);
/** Inline-logo upload envelope accepted ONLY by the CP write API (never stored). */
const InlineLogosSchema = z.object({
	lightSvgBase64: z.string().max(7e5).optional(),
	darkSvgBase64: z.string().max(7e5).optional(),
	faviconSvgBase64: z.string().max(2e5).optional()
}).strict();
/** The CP write API body: a brand record PLUS optional inline SVGs the server resolves to URLs. */
const BrandingWriteSchema = z.object({
	branding: OrgBrandingSchema,
	inlineLogos: InlineLogosSchema.optional()
}).strict();
//#endregion
export { BrandingWriteSchema, DeploymentBrandingSchema, InlineLogosSchema, OrgBrandingSchema };

//# sourceMappingURL=branding.schemas.js.map